import React, { useEffect, useRef, useState } from "react";

const InputTime = ({ name, value = "", onChange, placeholder, required = false }) => {
  const [inputValue, setInputValue] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const timePickerRef = useRef(null);

  // Convert 24-hour HH:mm from backend
  // to 12-hour display format
  const formatTimeForDisplay = (time) => {
    if (!time) return "";

    const [hours, minutes] = time.split(":");

    if (hours === undefined || minutes === undefined) {
      return "";
    }

    let hour = parseInt(hours, 10);

    if (isNaN(hour)) return "";

    const period = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;

    if (hour === 0) {
      hour = 12;
    }

    return `${String(hour).padStart(2, "0")}:${minutes} ${period}`;
  };

  useEffect(() => {
    setInputValue(formatTimeForDisplay(value));
  }, [value]);

  // Convert user input into HH:mm
  const parseTime = (input) => {
    if (!input) return "";

    let text = input.trim().toUpperCase();

    let period = "";

    if (text.includes("AM")) {
      period = "AM";
      text = text.replace(/\s*AM/g, "");
    } else if (text.includes("PM")) {
      period = "PM";
      text = text.replace(/\s*PM/g, "");
    }

    // Remove everything except numbers and :
    text = text.replace(/[^\d:]/g, "");

    let hours;
    let minutes;

    // 1230 or 12:30
    if (text.includes(":")) {
      const parts = text.split(":");

      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    } else if (text.length === 4) {
      hours = parseInt(text.substring(0, 2), 10);
      minutes = parseInt(text.substring(2, 4), 10);
    } else if (text.length === 3) {
      hours = parseInt(text.substring(0, 1), 10);
      minutes = parseInt(text.substring(1, 3), 10);
    } else {
      return "";
    }

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      minutes < 0 ||
      minutes > 59
    ) {
      return "";
    }

    // If AM/PM is provided, use 12-hour format
    if (period) {
      if (hours < 1 || hours > 12) {
        return "";
      }

      if (period === "AM" && hours === 12) {
        hours = 0;
      }

      if (period === "PM" && hours !== 12) {
        hours += 12;
      }
    } else {
      // Without AM/PM, assume 24-hour input
      // 1230 => 12:30
      if (hours > 23) {
        return "";
      }
    }

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}`;
  };

  const handleManualChange = (e) => {
    const typedValue = e.target.value;

    // IMPORTANT:
    // Don't force formatting while typing.
    // This allows normal typing and deleting.
    setInputValue(typedValue);

    setErrMsg("");
  };

  const handleBlur = () => {
    if (!inputValue.trim()) {
      if (required) {
        setErrMsg("Time is required.");
      } else {
        setErrMsg("");
      }
      onChange({
        target: {
          name,
          value: "",
        },
      });
      return;
    }

    const parsedTime = parseTime(inputValue);

    if (!parsedTime) {
      setErrMsg(
        "Please enter a valid time, e.g. 12:30 PM or 1230.",
      );
      return;
    }

    // Convert backend HH:mm to display format
    const displayTime = formatTimeForDisplay(parsedTime);

    setInputValue(displayTime);

    // Send HH:mm to parent/backend
    onChange({
      target: {
        name,
        value: parsedTime,
      },
    });

    setErrMsg("");
  };

  const handleTimePickerChange = (e) => {
    const selectedTime = e.target.value;

    setInputValue(formatTimeForDisplay(selectedTime));

    onChange({
      target: {
        name,
        value: selectedTime,
      },
    });

    setErrMsg("");
  };

  const openTimePicker = () => {
    if (timePickerRef.current?.showPicker) {
      timePickerRef.current.showPicker();
    } else {
      timePickerRef.current?.focus();
    }
  };

  return (
    <div className="form-group position-relative">
      <label htmlFor={name} className="col-form-label">
        {placeholder}
        {required && <span className="text-danger">*</span>}
      </label>

      <div className="position-relative">
        {/* Manual input */}
        <input
          type="text"
          className="form-control"
          name={name}
          value={inputValue}
          onChange={handleManualChange}
          onBlur={handleBlur}
          placeholder="hh:mm AM/PM"
        />

        {/* Time picker icon */}
        <button
          type="button"
          onClick={openTimePicker}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: "18px",
          }}
        >
          🕐
        </button>

        {/* Native time picker */}
        <input
          ref={timePickerRef}
          type="time"
          value={value || ""}
          onChange={handleTimePickerChange}
          tabIndex="-1"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      </div>

      {errMsg && (
        <small className="text-danger">
          {errMsg}
        </small>
      )}
    </div>
  );
};

export default InputTime;